import { memo } from 'react';
import type { CondimentDTO } from '@six-pockets/shared';
import { Stepper } from '@/components/ui';
import { formatINR } from '@/utils/format';
import { cx } from '@/utils/cx';
import type { QtyMap } from '../utils';
import styles from '@/styles/components/CondimentStepperList/style.scss';

interface CondimentStepperListProps {
  condiments: CondimentDTO[];
  qty: QtyMap;
  /** Pass a stable function (useCallback) so unchanged rows skip re-rendering. */
  onChange: (condimentId: string, qty: number) => void;
  /** Show each item's price next to its name. */
  showPrice?: boolean;
  /** `grid` lays items out in columns (wide containers such as the bill modal). */
  layout?: 'list' | 'grid';
}

interface StepperRowProps {
  condiment: CondimentDTO;
  value: number;
  onChange: (condimentId: string, qty: number) => void;
  showPrice?: boolean;
}

/** One condiment and its stepper. Memoised: a + / − re-renders only the row whose count changed. */
const StepperRow = memo(function StepperRow({ condiment: c, value, onChange, showPrice }: StepperRowProps) {
  return (
    <li className={styles.row}>
      <span className={styles.name}>
        {c.name}
        {showPrice && <span className={styles.price}>{formatINR(c.sellingPrice)}</span>}
      </span>
      <Stepper label={c.name} value={value} onChange={(n) => onChange(c.id, n)} />
    </li>
  );
});

/** One row per condiment with a − / + stepper. Used by the bill modal's tab editor and the tab form. */
export function CondimentStepperList({ condiments, qty, onChange, showPrice, layout = 'list' }: CondimentStepperListProps) {
  return (
    <ul className={cx(styles.list, layout === 'grid' ? styles.asGrid : styles.asList)}>
      {condiments.map((c) => (
        <StepperRow key={c.id} condiment={c} value={qty[c.id] ?? 0} onChange={onChange} showPrice={showPrice} />
      ))}
    </ul>
  );
}
